import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ChatMedia from "@/components/ChatMedia";
import { ShieldAlert, MessageSquare, ArrowLeft, User, Flag, CheckCircle2, Ban, Trash2 } from "lucide-react";

export default function Moderation() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [reports, setReports] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/conversations").then(({ data }) => setConvs(data));
    api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {});
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      Promise.all([
        api.get("/admin/conversations").then(({ data }) => setConvs(data)),
        api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {}),
      ]).finally(() => setLoading(false));
    } else if (user === false || (user && user.role !== "admin")) {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/admin/conversations/${activeId}/messages`).then(({ data }) => setThread(data));
  }, [activeId]);

  const openReportsFor = (cid) => reports.filter((r) => r.conversation_id === cid && r.status === "open");

  const resolveReports = async (cid) => {
    const open = openReportsFor(cid);
    try {
      await Promise.all(open.map((r) => api.post(`/admin/reports/${r.id}/resolve`)));
      toast.success("Melding afgehandeld");
      load();
    } catch (err) {
      toast.error("Kon melding niet afhandelen");
    }
  };

  const deleteMedia = async (mid) => {
    if (!window.confirm("Dit bestand definitief verwijderen? Beide gebruikers zien het daarna niet meer.")) return;
    try {
      await api.delete(`/admin/messages/${mid}/media`);
      toast.success("Bestand verwijderd");
      setThread((t) => ({ ...t, messages: t.messages.map((m) => (m.id === mid ? { ...m, media: undefined, media_removed: true } : m)) }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon bestand niet verwijderen");
    }
  };

  const banUser = async (userId, banned, label) => {
    if (banned && !window.confirm(`Weet je zeker dat je ${label} wilt blokkeren? Hun advertenties worden verborgen en ze kunnen niet meer inloggen.`)) return;
    try {
      await api.post(`/admin/users/${userId}/ban?banned=${banned}`);
      toast.success(banned ? `${label} is geblokkeerd` : `${label} is gedeblokkeerd`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Actie mislukt");
    }
  };

  if (user === null || loading) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Laden...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="h-4 w-4 text-rose-400" />
        <span className="font-mono text-xs uppercase tracking-widest text-rose-400">Beheer · Moderatie</span>
      </div>
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-2">Gesprekken meelezen</h1>
      <p className="text-sm text-zinc-500 mb-6">Bekijk alle gesprekken tussen kopers en verkopers bij problemen of meldingen.</p>

      {convs.length === 0 ? (
        <div className="text-center py-24 rounded-xl border border-zinc-800 bg-zinc-900/40">
          <MessageSquare className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
          <p className="font-serif text-xl text-zinc-300">Nog geen gesprekken</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-[minmax(0,1fr)] gap-0 rounded-xl border border-zinc-800 overflow-hidden h-[calc(100dvh-220px)] min-h-[420px] md:h-[75vh]">
          {/* List */}
          <div className={`min-h-0 border-r border-zinc-800 bg-zinc-950 overflow-y-auto ${activeId ? "hidden md:block" : "block"}`}>
            {convs.map((c) => (
              <button
                key={c.id}
                data-testid={`admin-conversation-${c.id}`}
                onClick={() => setActiveId(c.id)}
                className={`w-full flex items-center gap-3 p-4 border-b border-zinc-800/60 text-left transition-colors ${activeId === c.id ? "bg-zinc-900" : "hover:bg-zinc-900/50"}`}
              >
                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-zinc-800">
                  {c.product_image ? <img src={imgUrl(c.product_image)} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-200 truncate flex items-center gap-1.5">
                    {c.reported && <Flag className="h-3 w-3 text-rose-400 shrink-0" data-testid={`reported-badge-${c.id}`} />}
                    <span className="truncate">{c.product_title}</span>
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{c.buyer_name} ↔ {c.seller_display}</div>
                  <div className="text-[11px] text-zinc-600 truncate mt-0.5">{c.message_count} berichten · {c.last_message}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Thread */}
          <div className={`md:col-span-2 min-h-0 flex-col bg-zinc-950 ${activeId ? "flex" : "hidden md:flex"}`}>
            {thread ? (
              <>
                <div className="flex flex-wrap items-center gap-3 border-b border-zinc-800 p-4">
                  <button className="md:hidden text-zinc-400" onClick={() => { setActiveId(null); setThread(null); }}><ArrowLeft className="h-5 w-5" /></button>
                  <div className="min-w-[200px] flex-1 basis-[200px]">
                    <div className="text-sm font-medium text-zinc-200 break-words">{thread.conversation.product_title}</div>
                    <div data-testid="mod-thread-participants" className="text-xs text-zinc-500 [overflow-wrap:anywhere]">
                      Koper: {convs.find((c) => c.id === activeId)?.buyer_email} · Verkoper: {convs.find((c) => c.id === activeId)?.seller_email}
                    </div>
                  </div>
                  {(() => {
                    const c = convs.find((x) => x.id === activeId);
                    if (!c) return null;
                    return (
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {[
                          { id: c.buyer_id, banned: c.buyer_banned, label: c.buyer_name, key: "buyer" },
                          { id: c.seller_id, banned: c.seller_banned, label: c.seller_display, key: "seller" },
                        ].map((p) => (
                          <button
                            key={p.key}
                            data-testid={`ban-${p.key}-button`}
                            onClick={() => banUser(p.id, !p.banned, p.label)}
                            title={`${p.banned ? "Deblokkeer" : "Blokkeer"} ${p.label}`}
                            className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium transition-colors ${p.banned ? "border-emerald-800/60 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/50" : "border-rose-900/60 bg-rose-950/20 text-rose-300 hover:bg-rose-950/50"}`}
                          >
                            <Ban className="h-3 w-3" /> {p.banned ? "Deblokkeer" : "Blokkeer"} {p.key === "buyer" ? "koper" : "verkoper"}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div data-testid="mod-thread-messages" className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
                  <div className="text-center text-[11px] text-rose-400/70 bg-rose-950/20 rounded-lg py-2 px-3">
                    Moderatieweergave — je leest dit gesprek als beheerder. Wees discreet.
                  </div>
                  {openReportsFor(activeId).length > 0 && (
                    <div data-testid="report-banner" className="rounded-lg border border-rose-800/60 bg-rose-950/30 p-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-rose-300">
                          <Flag className="h-3.5 w-3.5" /> Gemeld door gebruiker ({openReportsFor(activeId).length})
                        </div>
                        <button
                          data-testid="resolve-report-button"
                          onClick={() => resolveReports(activeId)}
                          className="flex items-center gap-1 rounded-full bg-rose-600 hover:bg-rose-500 px-3 py-1 text-[11px] font-medium text-white transition-colors"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Markeer afgehandeld
                        </button>
                      </div>
                      <ul className="space-y-1">
                        {openReportsFor(activeId).map((r) => (
                          <li key={r.id} className="text-xs text-rose-200/90">
                            "{r.reason}" <span className="text-rose-300/50">— {r.reporter_name || "gebruiker"}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {thread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_is_seller ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] md:max-w-[75%] break-words whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.sender_is_seller ? "bg-zinc-800 text-zinc-100 rounded-br-sm" : "bg-rose-900/40 text-zinc-100 rounded-bl-sm"}`}>
                        <div className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60 flex flex-wrap items-center gap-1">
                          <User className="h-2.5 w-2.5" /> {m.sender_name} {m.sender_is_seller ? "(verkoper)" : "(koper)"}
                          <span className="normal-case tracking-normal">· {new Date(m.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        {m.media && (
                          <div className="space-y-1.5">
                            {m.media_reported && <div data-testid={`mod-media-reported-${m.id}`} className="flex items-center gap-1 text-[11px] font-semibold text-rose-300"><Flag className="h-3 w-3" /> Gemeld als ongepast</div>}
                            <ChatMedia media={m.media} mine testid={`mod-media-${m.id}`} />
                            <button data-testid={`mod-delete-media-${m.id}`} onClick={() => deleteMedia(m.id)}
                              className="flex items-center gap-1 rounded-full bg-rose-700 hover:bg-rose-600 px-3 py-1 text-[11px] font-medium text-white transition-colors">
                              <Trash2 className="h-3 w-3" /> Verwijder {m.media.type === "video" ? "video" : "foto"}
                            </button>
                          </div>
                        )}
                        {m.media_removed && <div data-testid={`mod-media-removed-${m.id}`} className="text-xs italic opacity-60">Bestand verwijderd door beheerder</div>}
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-zinc-600">Selecteer een gesprek om mee te lezen</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
